<template>
  <div>
    <SchemaTablePage
      ref="tablePageRef"
      layout-mode="tab"
      show-summary
      :search-schema="searchSchema"
      :columns="tableColumns"
      :default-query="defaultQuery"
      :request="request"
      :build-params="buildParams"
      :parse-result="parseResult"
      :table-attrs="tableAttrs"
    >
      <template #search-amountRange="{ query }">
        <div class="amount-range">
          <el-input-number v-model="query.minOrderAmount" :controls="false" placeholder="最小金额" :min="0" />
          <span class="range-separator">至</span>
          <el-input-number v-model="query.maxOrderAmount" :controls="false" placeholder="最大金额" :min="0" />
        </div>
      </template>

      <template #colMemberAccount="{ row }">
        <MemberAccountCell :row="row" :username="row.memberAccount" :uid="row.uid" :vip-level="row.memberLevel" />
      </template>
      <template #colOrderStatus="{ row }">
        <div v-if="row.status === 6" class="status-fail-wrap">
          <el-tag size="mini" :type="getTagType(row.status)?.type">{{ getTagType(row.status)?.label }}</el-tag>
          <el-popover :content="`失败原因: ${row.thirdPayError || '--'}`" placement="top" trigger="hover">
            <div slot="reference"><i class="el-icon-info info-icon" /></div>
          </el-popover>
        </div>
        <el-tag v-else size="mini" :type="getTagType(row.status)?.type">{{ getTagType(row.status)?.label }}</el-tag>
      </template>
      <template #colActions="{ row }">
        <el-button v-if="canWithdrawRefund(row.status)" type="text" size="small" @click="openRefundDialog(row)">
          退款
        </el-button>
      </template>

      <template #table-header>
        <el-button type="primary" size="mini" :loading="exporting" :disabled="!payoutExportReady" @click="handleExport">
          导出 Excel
        </el-button>
      </template>
    </SchemaTablePage>

    <WithdrawRefundDialog :visible.sync="refundDialogVisible" :row="refundRow" @success="handleRefundSuccess" />
  </div>
</template>

<script>
import { exportPayoutHistory, getPayoutHistory } from '@/api/finance/moneyManagement'
import {
  PAYOUT_EXPORT_COLUMNS,
  buildExportSheet,
  createPayoutExportFormatCell,
  isPayoutExportReady,
  parseExportList,
} from '@/pages/admin/finance/utils/financeExport'
import { buildExportFilename, downloadExcel } from '@/utils/exportExcel'
import { WITHDRAW_STATUS_OPTIONS } from '@/const'
import { centsToYuan, formatToLocal, yuanToCents } from '@/utils/amount'
import { formatTimestamp, toUnixSeconds } from '@/utils/time'
import MemberAccountCell from '@/components/MemberAccountCell.vue'
import SchemaTablePage from '@/components/SchemaTablePage'
import { canWithdrawRefund } from './config'
import WithdrawRefundDialog from './WithdrawRefundDialog.vue'

export default {
  name: 'ManageWithdrawHistoryTab',
  components: { MemberAccountCell, SchemaTablePage, WithdrawRefundDialog },
  data() {
    return {
      canWithdrawRefund,
      WITHDRAW_STATUS_OPTIONS,
      refundDialogVisible: false,
      refundRow: null,
      payoutExportReady: isPayoutExportReady(),
      exporting: false,
      defaultQuery: {
        memberAccount: '',
        orderNo: '',
        thirdOrderNo: '',
        minOrderAmount: 0,
        maxOrderAmount: 0,
        paymentChannelName: '',
        status: 0,
        applyTimeRange: [],
        completeTimeRange: [],
      },
      searchSchema: [
        {
          prop: 'memberAccount',
          label: '会员账号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'orderNo',
          label: '订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'thirdOrderNo',
          label: '三方订单号',
          component: 'input',
          componentProps: { clearable: true },
        },
        {
          prop: 'status',
          label: '订单状态',
          component: 'select',
          componentProps: { clearable: true },
          options: WITHDRAW_STATUS_OPTIONS,
        },
        {
          prop: 'applyTimeRange',
          label: '申请时间',
          component: 'datetime-range',
          componentProps: { clearable: true },
          span: { xs: 24, sm: 12, md: 10 },
        },
        {
          prop: 'completeTimeRange',
          label: '完成时间',
          component: 'datetime-range',
          componentProps: { clearable: true },
          span: { xs: 24, sm: 12, md: 10 },
        },
        {
          key: 'amountRange',
          label: '订单金额',
          component: 'slot',
          slot: 'search-amountRange',
        },
        {
          prop: 'paymentChannelName',
          label: '代付通道',
          component: 'input',
          componentProps: { clearable: true },
        },
        { component: 'actions' },
      ],
      tableColumns: [
        { prop: 'orderNo', label: '订单号', minWidth: 180, resizable: false },
        { prop: 'thirdOrderNo', label: '三方订单号', minWidth: 200, resizable: false },
        { prop: 'memberAccount', label: '会员账号', minWidth: 140, resizable: false, slot: 'colMemberAccount' },
        { prop: 'orderAmount', label: '订单金额', width: 100, resizable: false },
        { prop: 'totalFee', label: '总手续费', width: 100, resizable: false },
        { prop: 'accountType', label: '账号类型', width: 80, resizable: false },
        { prop: 'bankName', label: '银行名称', minWidth: 200, resizable: false },
        { prop: 'account', label: '收款账号', minWidth: 120, resizable: false },
        { prop: 'paymentChannelName', label: '代付通道', width: 80, resizable: false },
        { prop: 'applyTime', label: '申请时间', minWidth: 160, resizable: false },
        { prop: 'completeTime', label: '完成时间', minWidth: 160, resizable: false },
        { prop: 'msg', label: '备注', minWidth: 140, resizable: false },
        {
          fixed: 'right',
          prop: 'status',
          label: '订单状态',
          width: 100,
          align: 'center',
          resizable: false,
          slot: 'colOrderStatus',
        },
        {
          fixed: 'right',
          prop: 'action',
          label: '操作',
          width: 80,
          align: 'center',
          resizable: false,
          slot: 'colActions',
        },
      ],
      tableAttrs: {
        size: 'mini',
        showSummary: true,
        summaryMethod: (...args) => this.getSummaries(...args),
      },
    }
  },
  methods: {
    request(params) {
      return getPayoutHistory(params)
    },
    getTagType(status) {
      let _status = 0
      if ([1, 2, 3, 4].includes(status)) {
        _status = 4
      } else {
        _status = status
      }
      return {
        label: WITHDRAW_STATUS_OPTIONS.find(opt => opt.value === _status)?.label || '',
        type: WITHDRAW_STATUS_OPTIONS.find(opt => opt.value === _status)?.type || '',
      }
    },
    buildFilterParams(query) {
      let statusP = []
      if (query.status === 0 || query.status === '') {
        statusP = []
      } else if (query.status === 4) {
        statusP = [1, 2, 3, 4]
      } else {
        statusP = [query.status]
      }
      const params = { status: statusP }
      if (query.memberAccount) params.username = query.memberAccount
      if (query.orderNo) params.order_id = query.orderNo
      if (query.thirdOrderNo) params.third_order_id = query.thirdOrderNo
      if (query.minOrderAmount > 0) params.order_amount_start = yuanToCents(query.minOrderAmount)
      if (query.maxOrderAmount > 0) params.order_amount_end = yuanToCents(query.maxOrderAmount)
      if (query.paymentChannelName) params.payment_channel_name = query.paymentChannelName
      if (Array.isArray(query.applyTimeRange) && query.applyTimeRange.length === 2) {
        const [start, end] = query.applyTimeRange
        const startAt = toUnixSeconds(start)
        const endAt = toUnixSeconds(end)
        if (startAt) params.start_at = startAt
        if (endAt) params.end_at = endAt
      }
      if (Array.isArray(query.completeTimeRange) && query.completeTimeRange.length === 2) {
        const [start, end] = query.completeTimeRange
        const startAt = toUnixSeconds(start)
        const endAt = toUnixSeconds(end)
        if (startAt) params.updated_at_start = startAt
        if (endAt) params.updated_at_end = endAt
      }
      return params
    },
    buildParams({ query, page, limit }) {
      return {
        page,
        page_size: limit,
        ...this.buildFilterParams(query),
      }
    },
    buildExportParams(query) {
      return this.buildFilterParams(query)
    },
    async handleExport() {
      if (!this.payoutExportReady) {
        this.$message.info('提款导出字段与接口待配置')
        return
      }
      const query = this.$refs.tablePageRef?.query
      if (!query) return
      this.exporting = true
      try {
        const res = await exportPayoutHistory(this.buildExportParams(query))
        const list = parseExportList(res)
        if (!list.length) {
          this.$message.warning('暂无数据可导出')
          return
        }
        const formatCell = createPayoutExportFormatCell()
        const { header, data } = buildExportSheet(PAYOUT_EXPORT_COLUMNS, list, formatCell)
        await downloadExcel({
          header,
          data,
          filename: buildExportFilename('提款记录'),
        })
        this.$message.success('导出成功')
      } catch (err) {
        this.$message.error(err?.message || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    parseResult(res) {
      const respData = (res && res.data) || {}
      const list = respData.list || []
      return {
        list: list.map(item => this.mapWithdrawItem(item)),
        total: respData.total || 0,
      }
    },
    mapWithdrawItem(item) {
      return {
        uid: item.uid,
        orderNo: item.order_id,
        thirdOrderNo: item.third_order_id || '',
        memberAccount: item.username || '',
        memberLevel: item.vip_level != null ? String(item.vip_level) : '',
        orderAmount: centsToYuan(item.amount),
        totalFee: centsToYuan(item.total_fee),
        paymentInfo: item.cpf || '',
        applyTime: formatTimestamp(item.created_at),
        completeTime: formatTimestamp(item.updated_at),
        status: item.status,
        account: item.account || '',
        accountType: item.account_type || '',
        bankName: item.bank_name || '',
        paymentChannelName: item.payment_channel_name || '',
        msg: item.msg || '',
        thirdPayError: item.third_pay_error || '',
      }
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '小计（当前页）'
          return
        }
        if (column.property === 'orderAmount') {
          const values = data.map(item => parseFloat(item[column.property]) || 0)
          sums[index] = values.reduce((prev, curr) => {
            const value = parseFloat(curr)
            if (!isNaN(value)) {
              return prev + curr
            }
            return prev
          }, 0)
          sums[index] = formatToLocal(sums[index])
        } else {
          sums[index] = ''
        }
      })
      return sums
    },
    openRefundDialog(row) {
      this.refundRow = row
      this.refundDialogVisible = true
    },
    handleRefundSuccess() {
      this.$refs.tablePageRef?.reload?.()
    },
  },
}
</script>

<style scoped>
.amount-range {
  display: flex;
  align-items: center;
  width: 100%;
}

.amount-range .el-input-number {
  flex: 1;
  min-width: 0;
}

.range-separator {
  flex-shrink: 0;
  margin: 0 6px;
  color: #909399;
}

.status-fail-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.info-icon {
  color: #e6a23c;
  font-size: 12px;
}
</style>
